import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, message, Checkbox } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { panelApi } from '@services/api'
import {
  isPanelRememberEnabled,
  loadRememberedCredentials,
  persistPanelSession,
  refreshPanelSession,
  saveRememberedCredentials,
} from '../utils/panelSession'

const { Title, Text } = Typography

interface PanelLoginFormData {
  username: string
  password: string
  remember?: boolean
}

const PanelLogin: React.FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm<PanelLoginFormData>()
  const [loading, setLoading] = useState(false)
  const [tokenName, setTokenName] = useState<string | null>(null)
  const [username, setUsername] = useState('')
  const [remember, setRemember] = useState(isPanelRememberEnabled)

  useEffect(() => {
    const saved = loadRememberedCredentials()
    if (saved) {
      form.setFieldsValue({
        username: saved.username,
        password: saved.password,
        remember: true,
      })
      setUsername(saved.username)
      setRemember(true)
    }
  }, [form])

  useEffect(() => {
    const pt = localStorage.getItem('panel_token')
    const tid = localStorage.getItem('panel_token_id')
    if (!pt || !tid) return
    refreshPanelSession()
      .then((res) => {
        if (res) navigate(`/tokens/${res.token_id}/orders`, { replace: true })
      })
      .catch(() => {})
  }, [navigate])

  useEffect(() => {
    const uname = username.trim()
    if (!uname) {
      setTokenName(null)
      return
    }
    const timer = window.setTimeout(() => {
      panelApi.getLoginBranding(uname)
        .then((res) => setTokenName(res.token_name))
        .catch(() => setTokenName(null))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [username])

  const onFinish = async (values: PanelLoginFormData) => {
    if (loading) return
    setLoading(true)
    try {
      const res = await panelApi.login(values.username, values.password)
      persistPanelSession(res.access_token, res.token_id)
      saveRememberedCredentials(values.username, values.password, !!values.remember)
      message.success('登录成功')
      navigate(`/tokens/${res.token_id}/orders`)
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '登录失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '12px', background: '#f0f2f5' }}>
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 4 }}>订单管理面板</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
          {tokenName || '请输入用户名以识别账户'}
        </Text>
        <Form form={form} onFinish={onFinish} size="large" initialValues={{ remember: remember }}>
          <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
            />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="remember" valuePropName="checked">
            <Checkbox
              onChange={(e) => {
                setRemember(e.target.checked)
                if (!e.target.checked) saveRememberedCredentials('', '', false)
              }}
            >
              记住密码
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12 }}>
          登录有效期 1 天，进入面板后自动续期
        </Text>
      </Card>
    </div>
  )
}

export default PanelLogin
